import React, { ReactNode, Suspense, useEffect, useState } from 'react';
import {
  useAuth,
  PageLoader,
  ClientTokenManagerProvider,
  parseToken,
  ClientModel,
  userStorage,
} from '@cko/dashboard-shared';
import { useAuthInlineHookStatus } from 'auth/AuthTokenManager/useAuthInlineHookStatus';

const AuthError = React.lazy(() => import('components/AuthError/AuthError'));

export const AuthTokenManager = (props: { children: ReactNode }) => {
  const { authService } = useAuth();
  const { isLoading, errorCode } = useAuthInlineHookStatus();
  const [accessToken, setAccessToken] = useState<string | undefined>();

  const getAndSetAccessToken = async () => {
    let token = await authService.getAccessToken();
    if (!token) {
      // eslint-disable-next-line no-console
      console.warn("Access token can't be retrieved.");
      return;
    }

    const isSubEntitySession = Boolean(parseToken(token).cko_sub_entity_id);

    const previousClient: ClientModel | undefined = userStorage.getClient();

    if (!isSubEntitySession && previousClient?.id) {
      try {
        const newAccessToken = await authService.getTokenForMerchant(previousClient?.id);
        if (newAccessToken) {
          token = newAccessToken;
        }
      } catch (error) {
        // eslint-disable-next-line no-console
        console.warn('no access token for stored client session');
        userStorage.clearAll();
      }
    }

    setAccessToken(token);
  };

  useEffect(() => {
    if (!isLoading && !errorCode && !accessToken) {
      getAndSetAccessToken();
    }
  }, [isLoading]);

  const { children } = props;

  return (
    <Suspense fallback={<PageLoader />}>
      {errorCode && <AuthError errorCode={errorCode} />}
      {isLoading && <PageLoader />}
      {!isLoading && !errorCode && accessToken && (
        <ClientTokenManagerProvider
          initialClientID={parseToken(accessToken).cko_client_id || ''}
          initialToken={accessToken}
        >
          {children}
        </ClientTokenManagerProvider>
      )}
    </Suspense>
  );
};
